import { ArrowUpRight, Search } from "lucide-react";
import { RelativeTime } from "@/components/relative-time";
import { PageHeader } from "@/components/page-header";
import { ReportsSkeleton } from "@/components/page-skeletons";
import { ViewFallback } from "@/components/page-states";
import { StatusBadge } from "@/components/status-badge";
import { WorkspaceBanner } from "@/components/workspace-banner";
import { useT } from "@shared/client/locale";
import { Link } from "@shared/client/navigation";
import { usePageTitle, useView } from "@shared/client/view";
import { Button } from "@shared/components/ui/button";
import { Card } from "@shared/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@shared/components/ui/table";

type RunRow = {
  id: string;
  product_url: string;
  status: string;
  source: string | null;
  error_message: string | null;
  created_at: string;
  started_at?: string | null;
  reports: { offer_name: string | null } | Array<{ offer_name: string | null }> | null;
};

function offerNameOf(run: RunRow) {
  const report = Array.isArray(run.reports) ? run.reports[0] : run.reports;
  return report?.offer_name || null;
}

function hostOf(url: string) {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}


type ReportsData = { view: { clientName: string } | null; offerName: string | null; runs: RunRow[] };

export default function ReportsPage() {
  const t = useT();
  const copy = t.reports.list;
  usePageTitle(copy.pageTitle);
  const { data, error } = useView<ReportsData>("/api/views/reports");
  if (!data) return <ViewFallback error={error} skeleton={<ReportsSkeleton />} subject={copy.subject} />;
  const { view, offerName, runs } = data;

  return (
    <>
      {view && (
        <WorkspaceBanner>
          {copy.workspaceBanner.before}<strong>{view.clientName}</strong>{copy.workspaceBanner.after}
        </WorkspaceBanner>
      )}

      <PageHeader
        eyebrow={copy.eyebrow}
        lede={copy.lede}
        title={view ? copy.titleFor(view.clientName) : offerName ? copy.titleFor(offerName) : copy.title}
      />

      {runs.length === 0 ? (
        <Card className="items-center gap-3 py-14 text-center">
          <p className="m-0 text-[13px] text-muted-foreground">
            {view ? copy.emptyWorkspace : copy.empty}
          </p>
          {!view && (
            <Button asChild variant="outline">
              <Link href="/app#watch"><Search size={15} /> {copy.firstPass}</Link>
            </Button>
          )}
        </Card>
      ) : (
        <Card aria-label={copy.tableLabel} className="gap-0 overflow-hidden py-0">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-5 max-sm:pl-4">{copy.columns.pass}</TableHead>
                <TableHead className="max-sm:hidden">{copy.columns.startedAt}</TableHead>
                <TableHead className="max-sm:hidden">{copy.columns.status}</TableHead>
                <TableHead className="pr-5 text-right max-sm:pr-3"><span className="max-sm:sr-only">{copy.columns.report}</span></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.map((run) => (
                <TableRow key={run.id}>
                  <TableCell className="max-w-[380px] pl-5 max-sm:max-w-[240px] max-sm:pl-4">
                    <p className="m-0 truncate text-[13.5px] font-semibold tracking-tight">
                      {offerNameOf(run) || hostOf(run.product_url)}
                    </p>
                    <p className="m-0 truncate text-[12px] text-muted-foreground">{run.product_url}</p>
                    {/* Mobile : date et état sous le nom, leurs colonnes sont masquées. */}
                    <p className="m-0 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-muted-foreground sm:hidden">
                      <RelativeTime value={run.created_at} />
                      <StatusBadge status={run.status} />
                    </p>
                    {run.status === "failed" && run.error_message && (
                      <p className="m-0 mt-1 text-[12px] text-destructive">{run.error_message}</p>
                    )}
                  </TableCell>
                  <TableCell className="text-[12px] whitespace-nowrap text-muted-foreground tabular-nums max-sm:hidden">
                    <RelativeTime value={run.created_at} />
                  </TableCell>
                  <TableCell className="max-sm:hidden">
                    <span className="flex items-center gap-3">
                      <StatusBadge status={run.status} />
                      <span className="text-[12.5px] text-subtle-foreground">
                        {run.source === "radar" ? copy.source.radar : copy.source.manual}
                      </span>
                    </span>
                  </TableCell>
                  <TableCell className="pr-5 text-right max-sm:pr-3">
                    {run.status === "completed" ? (
                      <Button asChild size="sm" variant="ghost">
                        <Link aria-label={copy.openLabel} href={`/app/reports/${run.id}`}>
                          <span className="max-sm:hidden">{copy.open}</span> <ArrowUpRight size={14} />
                        </Link>
                      </Button>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </>
  );
}
